import type { ShopModuleKey } from '../utils/shopModules'

export enum OrderStatus {
  PENDING = 'pending',
  PAID = 'paid',
  RAMBURS = 'ramburs',
  PROCESSING = 'processing',
  SHIPPED = 'shipped',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded',
}

export enum PaymentStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  FAILED = 'failed',
  REFUNDED = 'refunded',
}

export enum PaymentMethod {
  CARD = 'card',
  NETOPIA = 'netopia',
  STRIPE = 'stripe',
  EUPLATESC = 'euplatesc',
  PAYU = 'payu',
  CASH_ON_DELIVERY = 'cash_on_delivery',
  BANK_TRANSFER = 'bank_transfer',
  TBI = 'tbi',
  ORDER_REQUEST = 'order_request',
}

export enum UserRole {
  CUSTOMER = 'customer',
  ADMIN = 'admin',
  MANAGER = 'manager',
  SUPER_ADMIN = 'super_admin',
}

export enum DiscountType {
  PERCENTAGE = 'percentage',
  FIXED = 'fixed',
}

export enum ShipmentStatus {
  PENDING = 'pending',
  AWB_GENERATED = 'awb_generated',
  PICKED_UP = 'picked_up',
  IN_TRANSIT = 'in_transit',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
}

export enum CourierProvider {
  SAMEDAY = 'sameday',
  CARGUS = 'cargus',
  FAN_COURIER = 'fan_courier',
  DPD = 'dpd',
  GLS = 'gls',
  TNT = 'tnt',
  FEDEX = 'fedex',
  UPS = 'ups',
  POSTA_ROMANA = 'posta_romana',
  OTHER = 'other',
}

export const ORDER_STATUS_LABELS: Record<string, string> = {
  pending: 'În așteptare',
  paid: 'Plătită',
  ramburs: 'Ramburs la livrare',
  processing: 'În procesare',
  shipped: 'Expediată',
  delivered: 'Finalizată',
  cancelled: 'Anulată',
  refunded: 'Returnată',
};

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  card: 'Card bancar',
  netopia: 'Card bancar (Netopia)',
  stripe: 'Card bancar (Stripe)',
  euplatesc: 'Card bancar (EuPlătesc)',
  payu: 'Card bancar (PayU)',
  cash_on_delivery: 'Ramburs la livrare',
  bank_transfer: 'Transfer bancar',
  tbi: 'Rate TBI Bank',
  order_request: 'Cerere de comandă',
}

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  pending: 'În așteptare',
  completed: 'Plătită',
  failed: 'Plată refuzată',
  refunded: 'Rambursată',
}

export const COURIER_LABELS: Record<string, string> = {
  sameday: 'Sameday',
  cargus: 'Cargus',
  fan_courier: 'FAN Courier',
  dpd: 'DPD',
  gls: 'GLS',
  tnt: 'TNT',
  fedex: 'FedEx',
  ups: 'UPS',
  posta_romana: 'Poșta Română',
  other: 'Alt curier',
}

export interface PaginationMeta {
  page: number
  limit: number
  total: number
  totalPages: number
}

export interface ApiResponse<T = unknown> {
  success: boolean
  data?: T
  message?: string
  errors?: Record<string, string[]>
  meta?: PaginationMeta
}

export interface ProductFilters {
  search?: string
  brandId?: number
  categoryId?: number
  minPrice?: number
  maxPrice?: number
  color?: string
  storage?: string
  inStock?: boolean
  minRating?: number
  sortBy?: 'price' | 'name' | 'createdAt' | 'rating' | 'discount'
  sortOrder?: 'asc' | 'desc'
  page?: number
  limit?: number
}

export interface CartItem {
  productId: number
  variantId?: number
  quantity: number
}

export interface JwtPayload {
  userId: number
  email: string
  role: UserRole
}

export interface User {
  id: number
  email: string
  firstName: string
  lastName: string
  phone?: string
  role: string
  roleId?: number
  avatar?: string
  emailVerified: boolean
  isActive: boolean
  hasPassword?: boolean
  googleId?: string | null
  appleId?: string | null
  facebookId?: string | null
  createdAt: string
}

export interface RoleOption {
  id: number
  name: string
}

export interface AuthTokens {
  accessToken: string
  refreshToken: string
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  phone?: string
  altcha?: string
}

export interface Brand {
  id: number
  name: string
  slug: string
  description?: string
  logo?: string
  website?: string
  isActive: boolean
  metaTitle?: string
  metaDescription?: string
}

export interface Category {
  id: number
  parentId?: number
  name: string
  slug: string
  description?: string
  /** HTML at the bottom of the category page. Only returned by `/categories/:slug` and `?all=true`. */
  longDescription?: string | null
  image?: string
  bannerImage?: string | null
  bannerLink?: string | null
  sortOrder: number
  isActive: boolean
  metaTitle?: string
  metaDescription?: string
  children?: Category[]
  productCount?: number
}

export interface ProductImage {
  id: number
  productId: number
  url: string
  alt?: string
  sortOrder: number
  isPrimary: boolean
  /** Frame for interactive 360° spin (ordered by sortOrder). */
  is360?: boolean
  /**
   * Links this gallery image to a variable-product attribute value.
   * Selecting that value on the PDP jumps to the first linked image.
   */
  attributeValueId?: number | null
}

export interface ProductVariant {
  id: number
  productId: number
  sku: string
  ean?: string | null
  color?: string
  storage?: string
  /** Attribute slug → value name (variable products) */
  options?: Record<string, string> | null
  price?: number
  salePrice?: number
  stock: number
  imei?: string
  isActive: boolean
}

export interface Product {
  id: number
  brandId?: number
  categoryId?: number
  name: string
  slug: string
  sku: string
  ean?: string | null
  productCode?: string
  imei?: string
  model?: string
  description?: string
  shortDescription?: string
  price: number
  salePrice?: number
  vatRate: number
  stock: number
  color?: string
  storage?: string
  specifications?: Record<string, string | number | boolean>
  productType?: 'simple' | 'complex' | 'package'
  isComplex?: boolean
  isPackage?: boolean
  isVariable?: boolean
  variationAttributes?: Array<{
    attributeId: number
    slug: string
    name: string
    pricingMode?: 'attribute' | 'combination'
    values: Array<{ id: number; name: string; slug?: string; imageUrl?: string | null; extraPrice?: number | null }>
  }> | null
  isActive: boolean
  isFeatured: boolean
  generatorId?: number | null
  /** When true, listing cards show a random gallery image. */
  randomCardImage?: boolean
  /** When true, checkout requires configured contract / ID document tasks */
  requiresContract?: boolean
  /** Generic role for contracts/documents: product, equipment, service, extra */
  itemRole?: 'product' | 'equipment' | 'service' | 'extra'
  /** Extra display prices per role (equipment / service / extra). */
  rolePrices?: {
    equipment?: number | null
    service?: number | null
    extra?: number | null
  } | null
  /** Selling unit label for services shops, e.g. m², oră. */
  priceUnit?: string | null
  /** When true, package groups count units and the customer can take more of the same product. */
  packageAllowMultiples?: boolean
  ratingAvg: number
  ratingCount: number
  metaTitle?: string
  metaDescription?: string
  seoKeywords?: string | null
  /** YouTube / Loom / Vimeo page URL. */
  videoUrl?: string | null
  shippingWeightKg?: number
  shippingLengthCm?: number
  shippingWidthCm?: number
  shippingHeightCm?: number
  shippingWeightKgRetur?: number
  shippingLengthCmRetur?: number
  shippingWidthCmRetur?: number
  shippingHeightCmRetur?: number
  shippingPriceTur?: number
  shippingPriceRetur?: number
  brand?: Brand
  category?: Category
  /** All assigned categories (many-to-many). Primary is also in `category` / `categoryId`. */
  categories?: Category[]
  images?: ProductImage[]
  variants?: ProductVariant[]
  reviews?: Review[]
  relatedProducts?: Product[]
  complementaryProducts?: Product[]
  attributeAssignments?: ProductAttributeAssignment[]
  complexFieldAssignments?: import('~/utils/complexProductFields').ProductComplexFieldAssignment[]
  packageItems?: import('~/utils/productPackage').ProductPackageItem[]
  packageCategoryRules?: import('~/utils/productPackage').ProductPackageCategoryRule[]
  packageChoiceGroups?: import('~/utils/productPackage').ProductPackageChoiceGroup[]
  createdAt: string
  updatedAt: string
}

export interface Review {
  id: number
  productId: number
  userId: number
  rating: number
  title?: string
  comment?: string
  isApproved: boolean
  authorName?: string | null
  reviewDate?: string | null
  verifiedPurchase?: boolean
  user?: Pick<User, 'firstName' | 'lastName'>
  createdAt: string
}

export interface CartLineItem {
  id?: number
  productId: number
  variantId?: number
  quantity: number
  product?: Product
  variant?: ProductVariant
  unitPrice: number
  totalPrice: number
  /** Snapshot of the product selling unit (m², oră, …). */
  priceUnit?: string | null
  customFields?: Array<{ fieldId: number; value: unknown }>
  customFieldsKey?: string
  customFieldsSnapshot?: Array<{
    fieldId: number
    name: string
    type: string
    value: unknown
    priceDelta: number
    hideFromCustomer?: boolean
    itemRole?: 'product' | 'equipment' | 'service' | 'extra'
  }>
  packageItemProductIds?: number[]
  /** Attribute slug → value name. Keeps each selection as its own cart line. */
  variationSelections?: Record<string, string>
}

export interface Cart {
  items: CartLineItem[]
  subtotal: number
  itemCount: number
}

export interface Address {
  id: number
  userId: number
  label: string
  firstName: string
  lastName: string
  phone: string
  street: string
  city: string
  county: string
  postalCode: string
  country: string
  isDefault: boolean
}

export interface OrderItem {
  id: number
  orderId: number
  productId?: number
  variantId?: number
  productName: string
  productSku?: string
  quantity: number
  unitPrice: number
  totalPrice: number
  priceUnit?: string | null
  customFields?: Array<{
    fieldId: number
    name: string
    type: string
    value: unknown
    priceDelta: number
    hideFromCustomer?: boolean
  }> | null
  product?: Product
}

export interface OrderStatusHistory {
  id: number
  orderId: number
  status: string
  comment?: string
  createdAt: string
}

export interface Payment {
  id: number
  orderId: number
  method: string
  status: string
  amount: number
  currency: string
  transactionId?: string
  netopiaOrderId?: string
  netopiaResponse?: Record<string, unknown>
  paidAt?: string
}

export interface AwbInfo {
  awbNumber: string
  courier: string
  status?: string
  pdfUrl?: string
  coleteOnlineId?: string
}

export interface Shipment {
  id: number
  orderId: number
  courier: string
  status: string
  shippingCost: number
  weight?: number
  trackingUrl?: string
  coleteServiceId?: number | null
  coleteActivationId?: string | null
  coleteServiceCategory?: string | null
  recipientShippingPointId?: number | null
  coleteCourierLabel?: string | null
  direction?: 'outbound' | 'return'
  awb?: {
    awbNumber: string
    pdfUrl?: string
  }
  awbs?: AwbInfo[]
}

export interface Order {
  id: number
  orderNumber: string
  userId?: number
  guestEmail?: string
  status: string
  subtotal: number
  discountAmount: number
  shippingCost: number
  taxAmount: number
  total: number
  adjustments?: Array<{ key: string, label: string, amount: number, points?: number }> | null
  couponCode?: string
  coupon?: {
    code: string
    discountType?: 'percentage' | 'fixed' | 'range'
    discountValue?: number
    description?: string | null
  }
  shippingFirstName?: string
  shippingLastName?: string
  shippingPhone?: string
  shippingStreet?: string
  shippingCity?: string
  shippingCounty?: string
  shippingPostalCode?: string
  shippingCountry?: string
  billingFirstName?: string
  billingLastName?: string
  billingStreet?: string
  billingCity?: string
  billingCounty?: string
  billingPostalCode?: string
  billingCountry?: string
  billingCui?: string
  notes?: string
  newsletterOptIn?: boolean
  phoneContactRequested?: boolean
  checkoutDocuments?: {
    required: boolean
    submittedAt: string
    tasks: Array<{
      taskId: string
      type: 'info' | 'contract' | 'id_document'
      label: string
      title: string
      acknowledged?: boolean
      contractHtml?: string
      contractPdfUrl?: string
      signatures?: Array<{
        fieldId: string
        type: string
        label: string
        value: string | boolean
        imageUrl?: string
      }>
      idDocument?: {
        frontUrl?: string
        backUrl?: string
        frontName?: string
        backName?: string
        cnp?: string
        series?: string
        number?: string
        firstName?: string
        lastName?: string
        nationality?: string
        birthDate?: string
        birthPlace?: string
        address?: string
        issuedBy?: string
        issuedAt?: string
        expiresAt?: string
      }
    }>
  } | null
  smartbillSeries?: string
  smartbillNumber?: string
  invoiceProvider?: 'smartbill' | 'oblio' | 'fgo'
  invoiceLink?: string
  acquisitionChannel?: string | null
  acquisition?: {
    channel?: string | null
    source?: string | null
    medium?: string | null
    campaign?: string | null
    content?: string | null
    term?: string | null
    referrer?: string | null
    landingPage?: string | null
    clickIdType?: string | null
    first?: {
      channel?: string | null
      source?: string | null
      medium?: string | null
      campaign?: string | null
      referrer?: string | null
      landingPage?: string | null
    } | null
  } | null
  user?: Pick<User, 'id' | 'email' | 'firstName' | 'lastName'>
  items?: OrderItem[]
  payment?: Payment
  payments?: Payment[]
  shipment?: Shipment
  shipments?: Array<Shipment & { awbs?: AwbInfo[] }>
  returnRequests?: Array<{
    id: number
    returnNumber: string
    status: string
    reason: string
    refundAmount?: number
    items?: Array<{
      id: number
      orderItemId: number
      productName: string
      quantity: number
    }>
    shipment?: {
      trackingUrl?: string | null
      awbs?: AwbInfo[]
    } | null
  }>
  statusHistory?: OrderStatusHistory[]
  createdAt: string
  updatedAt: string
}

export interface WishlistItem {
  id: number
  userId: number
  productId: number
  product: Product
  createdAt: string
}

export interface BannerButton {
  label: string
  to: string
  variant?: 'solid' | 'outline' | 'ghost' | 'soft' | 'link'
  color?: 'primary' | 'white' | 'neutral' | 'success' | 'error'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
}

export type BannerMediaType = 'image' | 'video'

export interface Banner {
  id: number
  title: string
  subtitle?: string
  titleColor?: string | null
  titleFontFamily?: string | null
  subtitleColor?: string | null
  subtitleFontFamily?: string | null
  mediaType?: BannerMediaType
  image?: string | null
  imageWidth?: number | null
  imageHeight?: number | null
  mobileImage?: string | null
  mobileImageWidth?: number | null
  mobileImageHeight?: number | null
  imageFit?: 'cover' | 'contain'
  overlayEnabled?: boolean
  link?: string
  position: string
  sortOrder: number
  isActive: boolean
  buttons?: BannerButton[]
  lottie?: Record<string, unknown> | null
}

export interface CmsPage {
  id: number
  title: string
  slug: string
  content?: string
  isActive: boolean
  metaTitle?: string
  metaDescription?: string
}

export type ProductCtaActionType = 'link' | 'modal'
export type ProductCtaVariant = 'solid' | 'outline' | 'soft' | 'ghost'
export type ProductCtaColor = 'primary' | 'neutral' | 'success' | 'error' | 'warning' | 'info'

export interface ProductCtaButton {
  id: number
  label: string
  actionType: ProductCtaActionType
  linkUrl?: string | null
  modalTitle?: string | null
  modalContentHtml?: string | null
  variant?: ProductCtaVariant
  color?: ProductCtaColor
  showOnProductPage: boolean
  sortOrder: number
  isActive: boolean
}

export interface Coupon {
  id: number
  code: string
  description?: string
  discountType: string
  discountValue: number
  discountRanges?: Array<{ min: number; max: number | null; percent: number }> | null
  minOrderAmount: number
  maxUses?: number | null
  usedCount: number
  startsAt?: string | null
  expiresAt?: string | null
  isActive: boolean
}

export interface CheckoutData {
  shippingFirstName: string
  shippingLastName: string
  shippingPhone: string
  shippingPhoneCountry?: string
  shippingStreet: string
  shippingCity: string
  shippingCounty: string
  shippingPostalCode: string
  shippingCountry: string
  billingFirstName?: string
  billingLastName?: string
  billingStreet?: string
  billingCity?: string
  billingCounty?: string
  billingPostalCode?: string
  billingCountry?: string
  billingCui?: string
  sameAsBilling?: boolean
  paymentMethod: string
  couponCode?: string
  notes?: string
  guestEmail?: string
  newsletterOptIn?: boolean
  phoneContactRequested?: boolean
}

export interface ColeteOnlineOffer {
  serviceId: number
  activationId?: string
  courierName: string
  serviceName: string
  priceTotal: number
  priceNoVat: number
  currency: string
  category: string
  requiresRecipientShippingPoint: boolean
  requiresSenderShippingPoint: boolean
}

export interface ColeteOnlineOfferGroup {
  category: string
  label: string
  offers: ColeteOnlineOffer[]
}

export interface CheckoutShippingSelection {
  serviceId: number
  activationId?: string
  serviceCategory: string
  courierName: string
  serviceName: string
  priceTotal: number
  recipientShippingPointId?: number
  weight?: number
  packages?: number
}

export interface ColeteShippingPoint {
  id: number
  name: string
  courierName?: string
  city: string
  county: string
  street: string
  postalCode: string
}

export interface AdminStats {
  totalOrders: number
  totalRevenue: number
  totalProducts: number
  totalUsers: number
  pendingOrders: number
  toShipOrders?: number
  lowStockProducts: number
  recentOrders: Order[]
  subscription?: ShopSubscriptionSummary | null
  today?: {
    orders: number
    revenue: number
    previousOrders: number
    previousRevenue: number
  }
  month?: {
    orders: number
    revenue: number
    previousOrders: number
    previousRevenue: number
  }
  series?: Array<{ date: string; orders: number; revenue: number }>
  statuses?: Array<{ status: string; count: number }>
  sources?: Array<{ channel: string; orders: number; revenue: number }>
  topProducts?: Array<{ name: string; quantity: number; revenue: number }>
}

export type ShopBillingInterval = 'monthly' | 'yearly'
export type ShopSubscriptionStatus = 'none' | 'active' | 'past_due' | 'trialing' | 'canceled'

export interface ShopSubscriptionSummary {
  configured: boolean
  paymentExempt: boolean
  billingInterval: ShopBillingInterval
  dueDay?: number
  dueMonth?: number
  monthlyAmount: number
  yearlyAmount?: number
  periodAmount: number
  chargeAmount?: number
  currency: string
  status: ShopSubscriptionStatus
  lastPaymentAt: string | null
  lastPaymentAmount: number | null
  nextPaymentAt: string | null
  trialEndsAt?: string | null
  trialActive?: boolean
  storefrontLocked?: boolean
  graceMonths?: number
  graceEndsAt?: string | null
  hasCard?: boolean
  stripeManaged?: boolean
  cancelAtPeriodEnd?: boolean
  lastInvoice?: {
    number: string | null
    url: string | null
    paidAt: string | null
  } | null
}

export interface ShopSubscriptionBilling {
  company: string
  legalName: string
  cui: string
  regCom: string
  address: string
  city: string
  county: string
  country: string
  email: string
  phone: string
}

export interface ShopSubscriptionPaymentRow {
  id: number | string
  amount: number
  currency: string
  paidAt: string | null
  method: string
  status: string
  notes: string | null
  number?: string | null
  hostedInvoiceUrl?: string | null
  invoicePdf?: string | null
  oblioSeriesName?: string | null
  oblioNumber?: string | null
  oblioLink?: string | null
  source?: 'stripe' | 'manual'
}

export interface ShopSubscriptionAddon {
  key: ShopModuleKey
  label: string
  description: string
  offered: boolean
  monthlyAmount: number
  yearlyAmount: number
  catalogMonthlyAmount?: number
  catalogYearlyAmount?: number
  customPrice?: boolean
  monthlyOverride?: number | null
  yearlyOverride?: number | null
  periodAmount: number
  setupAmount?: number
  catalogSetupAmount?: number
  setupOverride?: number | null
  setupDue?: number
  setupBilledAt?: string | null
  setupBilledAmount?: number | null
  currency: string
  entitled: boolean
  complimentary: boolean
  status: 'inactive' | 'active' | 'canceling'
  cancelAtPeriodEnd: boolean
  canActivate: boolean
  canCancel: boolean
  activateHint: string | null
  billFromNextAt?: string | null
}

export interface ShopSubscriptionDto extends ShopSubscriptionSummary {
  dueDay: number
  dueMonth: number
  hasCard: boolean
  billing: ShopSubscriptionBilling
  card: { brand: string, last4: string, expMonth: number | null, expYear: number | null } | null
  cardCollectionEnabled: boolean
  cardProvider?: 'none' | 'stripe'
  stripeManaged?: boolean
  cancelAtPeriodEnd?: boolean
  notes: string | null
  lastInvoice?: {
    number: string | null
    url: string | null
    paidAt: string | null
  } | null
  payments: ShopSubscriptionPaymentRow[]
  addons?: ShopSubscriptionAddon[]
}

export interface CompareProduct extends Product {
  compareKey: string
}

export interface FilterOption {
  label: string
  value: string | number
  count?: number
}

export interface ProductFilterState {
  search?: string
  brandIds?: number[]
  categoryId?: number
  models?: string[]
  minPrice?: number
  maxPrice?: number
  colors?: string[]
  storages?: string[]
  inStock?: boolean
  onSale?: boolean
  minRating?: number
  attributeValueIds?: number[]
  availabilityStatusIds?: number[]
  refurbishedOnly?: boolean
  sortBy?: 'price' | 'name' | 'createdAt' | 'rating' | 'discount' | 'recommended' | 'price-asc' | 'price-desc'
  sortOrder?: 'asc' | 'desc'
  page?: number
  limit?: number
}

export interface ProductAttribute {
  id: number
  name: string
  slug: string
  sortOrder: number
  isActive: boolean
  showOnCard?: boolean
  showInFilters?: boolean
  imageUrl?: string | null
  values?: ProductAttributeValue[]
}

export interface ProductAttributeAssignment {
  id?: number
  productId?: number
  attributeId: number
  valueId: number
  attribute?: ProductAttribute
  value?: ProductAttributeValue
}

export interface ProductAttributeValue {
  id: number
  attributeId: number
  name: string
  slug: string
  sortOrder: number
}

export interface ProductModelOption {
  id: number
  brandId: number
  name: string
  slug: string
}

export interface ProductFacets {
  brands: { id: number, name: string, count: number }[]
  models: { name: string, count: number }[]
  colors: { value: string, count: number }[]
  storages: { value: string, count: number }[]
  attributes: {
    id: number
    name: string
    slug: string
    top?: boolean
    values: { id: number, name: string, count: number }[]
  }[]
  /** Present only when the „La reducere” filter is enabled for the shop. */
  onSaleCount?: number
  /** Present only when the Statusuri & livrare module is on. */
  availability?: { id: number, name: string, count: number }[]
  /** Present only when the Resigilate module is on. */
  refurbishedCount?: number
}

export interface OrderTracking {
  orderNumber: string
  status: string
  statusHistory: OrderStatusHistory[]
  shipment?: Shipment
  estimatedDelivery?: string
}
